import { DefaultButton, PrimaryButton } from "OfficeFabric/Button";
import { Dialog, DialogFooter, DialogType } from 'OfficeFabric/Dialog';
import { FeatureAvailabilityFlags } from "Presentation/Scripts/TFS/Generated/TFS.Server.WebAccess.Constants";
import { TfsContext } from "Presentation/Scripts/TFS/TFS.Host.TfsContext";
import * as React from "react";
import { PullRequestPermissions } from "VersionControl/Scenarios/PullRequestDetail/Stores/PullRequestPermissionsStore";
import { Flux } from "VersionControl/Scenarios/PullRequestDetail/View/PullRequestDetail.Flux";
import { OperationCompletedProps } from "VersionControl/Scripts/Components/AsyncGitOperation/AsyncGitOperationTracker";
import { AsyncRefOperationControllerView } from "VersionControl/Scripts/Components/AsyncGitOperation/AsyncRefOperationControllerView";
import { CompleteMergePanel } from "VersionControl/Scripts/Components/PullRequestReview/CompleteMergePanel";
import { IPullRequestActions, PullRequestCompleteButton } from "VersionControl/Scripts/Components/PullRequestReview/PullRequestCompleteButton";
import { IPullRequestEllipsisButton, PullRequestEllipsisMenu } from "VersionControl/Scripts/Components/PullRequestReview/PullRequestEllipsisMenu";
import * as CustomerIntelligenceConstants from "VersionControl/Scripts/CustomerIntelligenceConstants";
import { GitRepositoryContext } from "VersionControl/Scripts/GitRepositoryContext";
import { PullRequestFollowStatus } from "VersionControl/Scripts/PullRequestFollowStatus";
import { RepositoryContext } from "VersionControl/Scripts/RepositoryContext";
import { PolicyCount } from "VersionControl/Scripts/Stores/PullRequestReview/PolicyCount";
import { IPullRequest } from "VersionControl/Scripts/Stores/PullRequestReview/PullRequestDetailStore";
import { PullRequestVoteStatus, ReviewerItem } from "VersionControl/Scripts/Utils/ReviewerUtils";
import { FeatureAvailabilityService } from "VSS/FeatureAvailability/Services";
import * as Telemetry from "VSS/Telemetry/Services";
import * as StringUtils from "VSS/Utils/String";
import { isEmptyGuid } from "VSS/Utils/String";
import VCContracts = require("TFS/VersionControl/Contracts");
import VCPullRequest = require("VersionControl/Scripts/TFS.VersionControl.PullRequest");
import VCResources = require("VersionControl/Scripts/Resources/TFS.Resources.VersionControl");

export interface IPullRequestActionBarState {
    hasRejectedPolicies: boolean;
    policyCount: PolicyCount;
    policiesAreLoading: boolean;
    workItems: number[];
    showCompleteDialog: boolean;
    useAutoComplete: boolean;
    liveUpdateEnabled: boolean;
    transitionWorkItemsIsEnabled: boolean;
    pullRequest: IPullRequest;
    repositoryContext: RepositoryContext;
    tfsContext: TfsContext;
    followStatus: PullRequestFollowStatus;
    reviewerItems: ReviewerItem[];
    isVotePrimaryAction: boolean;
    permissions: PullRequestPermissions;
    showResetVotesDialog : boolean;
    showDeleteBranchDialog: boolean;
    loading: boolean;
}

/**
 * The right side drop down buttons that complete PRs.
 */
export class PullRequestActionMenuContainer extends React.Component<{}, IPullRequestActionBarState> {

    private _moreActionsButton: IPullRequestEllipsisButton;

    constructor(props: null) {
        super(props);
        this._handleCompleteMergeResult = this._handleCompleteMergeResult.bind(this);
        this.state = this._getStateFromStores();
    }

    public render(): JSX.Element {
        if (this.state.loading) {
            return null;
        }

        const pullRequest = this.state.pullRequest;
        const repositoryContext = this.state.repositoryContext as GitRepositoryContext;

        return (
            <div className="vc-pullrequest-action-bar">
                {this._createActionButton()}
                <PullRequestEllipsisMenu
                    pullRequest={pullRequest}
                    followStatus={this.state.followStatus}
                    repositoryContext={repositoryContext}
                    reviewerItems={this.state.reviewerItems}
                    tfsContext={this.state.tfsContext}
                    commitAllComments={Flux.instance().actionCreator.discussionActionCreator.commitAllComments}
                    follow={Flux.instance().actionCreator.followsActionCreator.follow}
                    unfollow={Flux.instance().actionCreator.followsActionCreator.unfollow}
                    onViewCommit={Flux.instance().actionCreator.navigationActionCreator.viewCommit}
                    retryMergePullRequest={Flux.instance().actionCreator.pullRequestActionCreator.retryMergePullRequest}
                    mailSettingsEnabled={Flux.instance().actionCreator.followsActionCreator.isMailSettingsEnabled()}
                    liveUpdateEnabled={this.state.liveUpdateEnabled}
                    setLiveUpdate={Flux.instance().actionCreator.setLiveUpdate}
                    hasPermissionToAddComment={this.state.permissions.addEditComment}
                    hasPermissionToCherryPickRevert={this.state.permissions.cherryPickRevert}
                    hasPermissionToFollow={this.state.permissions.follow}
                    hasPermissionToRestartMerge={this.state.permissions.restartMerge}
                    hasPermissionToShare={this.state.permissions.share}
                    hasPermissionToLiveUpdate={this.state.permissions.liveUpdate}
                    componentRef={ref => this._moreActionsButton = ref} />
                {
                    this.state.showCompleteDialog &&
                    <CompleteMergePanel
                        autoComplete={this.state.useAutoComplete}
                        isOpen={this.state.showCompleteDialog}
                        transitionWorkItemsIsEnabled={this.state.transitionWorkItemsIsEnabled}
                        onResult={this._handleCompleteMergeResult}
                    />
                }
                {this.state.showDeleteBranchDialog && this._createDeleteBranchDialog()}
                {this.state.showResetVotesDialog && this._createUnpublishDialog()}
                <AsyncRefOperationControllerView
                    pullRequest={pullRequest}
                    repositoryContext={repositoryContext}
                    operationCompletedProps={this._getPrCreationProps()}/>
            </div>);
    }

    public componentDidMount(): void {
        Flux.instance().storesHub.clientPolicyEvaluationStore.addChangedListener(this._onStoresChanged);
        Flux.instance().storesHub.navigationStore.addChangedListener(this._onStoresChanged);
        Flux.instance().storesHub.featureAvailabilityStore.addChangedListener(this._onStoresChanged);
        Flux.instance().storesHub.pullRequestDetailStore.addChangedListener(this._onStoresChanged);
        Flux.instance().storesHub.contextStore.addChangedListener(this._onStoresChanged);
        Flux.instance().storesHub.followsStore.addChangedListener(this._onStoresChanged);
        Flux.instance().storesHub.reviewersStore.addChangedListener(this._onStoresChanged);
        Flux.instance().storesHub.permissionsStore.addChangedListener(this._onStoresChanged);
    }

    public componentWillUnmount(): void {
        Flux.instance().storesHub.clientPolicyEvaluationStore.removeChangedListener(this._onStoresChanged);
        Flux.instance().storesHub.navigationStore.removeChangedListener(this._onStoresChanged);
        Flux.instance().storesHub.featureAvailabilityStore.removeChangedListener(this._onStoresChanged);
        Flux.instance().storesHub.pullRequestDetailStore.removeChangedListener(this._onStoresChanged);
        Flux.instance().storesHub.contextStore.removeChangedListener(this._onStoresChanged);
        Flux.instance().storesHub.followsStore.removeChangedListener(this._onStoresChanged);
        Flux.instance().storesHub.reviewersStore.removeChangedListener(this._onStoresChanged);
        Flux.instance().storesHub.permissionsStore.removeChangedListener(this._onStoresChanged);
    }

    public componentWillUpdate(nextProps: null, nextState: IPullRequestActionBarState): void {
        const pullRequest = this.state.pullRequest;

        if (nextState.pullRequest.status === VCContracts.PullRequestStatus.Completed &&
            pullRequest && pullRequest.status === VCContracts.PullRequestStatus.Active) {

                // If the user just completed the pull request the Complete button will disappear from the UI
                // and the focus will move to the Body of the document.  We need to force the focus back to the Action Menu
                // in this scenario.

                if (this._moreActionsButton && document.body === document.activeElement) {
                    this._moreActionsButton.focus();
                }
        }
    }

    public shouldComponentUpdate(nextProps: {}, nextState: IPullRequestActionBarState): boolean {
        if (nextState.loading && this.state.loading) {
            return false; // if we are still loading, don't bother to re-render
        }

        return nextState.hasRejectedPolicies !== this.state.hasRejectedPolicies ||
            nextState.policyCount !== this.state.policyCount ||
            nextState.policiesAreLoading !== this.state.policiesAreLoading ||
            nextState.workItems !== this.state.workItems ||
            nextState.showCompleteDialog !== this.state.showCompleteDialog ||
            nextState.useAutoComplete !== this.state.useAutoComplete ||
            nextState.liveUpdateEnabled !== this.state.liveUpdateEnabled ||
            nextState.transitionWorkItemsIsEnabled !== this.state.transitionWorkItemsIsEnabled ||
            nextState.pullRequest !== this.state.pullRequest ||
            nextState.repositoryContext !== this.state.repositoryContext ||
            nextState.tfsContext !== this.state.tfsContext ||
            nextState.followStatus !== this.state.followStatus ||
            nextState.reviewerItems !== this.state.reviewerItems ||
            nextState.isVotePrimaryAction !== this.state.isVotePrimaryAction ||
            nextState.permissions !== this.state.permissions ||
            nextState.showResetVotesDialog !== this.state.showResetVotesDialog ||
            nextState.showDeleteBranchDialog !== this.state.showDeleteBranchDialog;
    }

    private _createActionButton(): JSX.Element {
        const pullRequest = this.state.pullRequest;
        const isCTA = !this.state.isVotePrimaryAction;

        if (pullRequest.status === VCContracts.PullRequestStatus.Active) {
            const branchStatus = pullRequest.branchStatusContract();

            if ((branchStatus && branchStatus.sourceBranchStatus && branchStatus.sourceBranchStatus.isDeleted)
                || (branchStatus && branchStatus.targetBranchStatus && branchStatus.targetBranchStatus.isDeleted)
            ) {
                return this._createAbandonButton();
            }

            if (pullRequest.externallyMerged) {
                if (pullRequest.isDraft) {
                    return this._createAbandonButton();
                } else {
                    return this._createCloseButon();
                }
            }

            const policyCount = this.state.policyCount;
            const canAutoComplete = !!(policyCount && policyCount.totalPolicies);
            let hasRejectedPolicies = this.state.hasRejectedPolicies;

            if (FeatureAvailabilityService.isFeatureEnabled(FeatureAvailabilityFlags.SourceControlGitPullRequestsEnforceAdHocRequiredReviewers, false)
                && this.state.reviewerItems.some(r => r.identity.isRequired && !(r.identity.vote > PullRequestVoteStatus.NONE))
            ) {
                // When this feature flag is set, we effectively treat required reviewerws as if they were policies even though there's no policy in place
                hasRejectedPolicies = true;
            }

            const isAutoCompleteSet = (pullRequest.autoCompleteSetBy
                && !isEmptyGuid(pullRequest.autoCompleteSetBy.id));

            const completionDisabledReason = this._completionDisabledReason();

            // show the split button
            return (
                <PullRequestCompleteButton
                    canAutoComplete={canAutoComplete}
                    completionDisabledReason={completionDisabledReason}
                    isAutoCompleteSet={isAutoCompleteSet}
                    isDraft={pullRequest.isDraft}
                    hasRejectedPolicies={hasRejectedPolicies}
                    isCTA={isCTA}
                    hasPermissionToAbandonReactivate={this.state.permissions.abandonReactivate}
                    hasPermissionToCancelAutoComplete={this.state.permissions.cancelAutoComplete}
                    hasPermissionToComplete={this.state.permissions.complete}
                    hasPermissionToPublishUnpublish={this.state.permissions.publishUnpublish}
                    pullRequestActions={{
                        abandonPullRequest: this._abandonPullRequest,
                        cancelAutoComplete: this._cancelAutoComplete,
                        setAutoComplete: this._setAutoComplete,
                        publish: this._publishPullRequest,
                        unpublish: this._unpublishPullRequestBegin,
                        completePullRequest: this._completePullRequest,
                    } as IPullRequestActions}
            />);
        }
        else if (pullRequest.canReactivate) {
            return this._createReactivateButton();
        }
        else if (pullRequest.status === VCContracts.PullRequestStatus.Completed) {
            return this.state.permissions.deleteSourceBranch && this._createDeleteSourceBranchButton();
        }
    }

    private _completionDisabledReason(): string {
        if (this.state.policiesAreLoading) {
            return VCResources.PullRequest_MergePolicies_CheckingPolicies;
        }

        const pullRequest: VCContracts.GitPullRequest = this.state.pullRequest.pullRequestContract();
        const branchStatus: VCPullRequest.IPullRequestBranchStatus = this.state.pullRequest.branchStatusContract();

        if (!pullRequest || !branchStatus) {
            return null;
        }

        if (!!pullRequest.isDraft) {
            return null;
        }

        if (pullRequest.mergeStatus === VCContracts.PullRequestAsyncStatus.Conflicts) {
            return VCResources.PullRequest_CannotComplete_MergeConflicts;
        }

        if (this.state.pullRequest.isMerging) {
            return VCResources.PullRequest_CannotComplete_MergeInProgress;
        }

        return null;
    }

    private _createCloseButon(): JSX.Element {
        return this.state.permissions.complete &&
            <button className="bowtie-widget btn-cta vc-pullrequest-action-button" onClick={this._completePullRequest}>
                {VCResources.PullRequest_ClosePullRequest}
            </button>;
    }

    private _createAbandonButton(): JSX.Element {
        return this.state.permissions.abandonReactivate &&
            <button className="bowtie-widget btn-cta vc-pullrequest-action-button" onClick={this._abandonPullRequest}>
                {VCResources.PullRequest_AbandonPullRequest}
            </button>;
    }

    private _createReactivateButton(): JSX.Element {
        return this.state.permissions.abandonReactivate &&
            <button className="bowtie-widget btn-cta vc-pullrequest-action-button" onClick={this._reactivatePullRequest}>
                {VCResources.PullRequest_ReactivatePullRequest}
            </button>;
    }

    private _createDeleteSourceBranchButton(): JSX.Element {
        const pullRequest = this.state.pullRequest;

        return (
            <button type="button"
                className={pullRequest.canDeleteSourceBranch ? "bowtie-widget btn-cta vc-pull-request-action-button" : "bowtie-widget"}
                key="deleteSourceBranch"
                disabled={!pullRequest.canDeleteSourceBranch}
                title={!pullRequest.canDeleteSourceBranch ? pullRequest.cannotDeleteReasonHint : null}
                onClick={() => this.setState({ showDeleteBranchDialog: true })}>
                {VCResources.PullRequest_DeleteSourceBranch}
            </button>);
    }

    private _createDeleteBranchDialog(): JSX.Element {
        // warn the user they are deleting the source branch
        return (
            <Dialog
                hidden={false}
                onDismiss={() => { this._deleteSourceBranchClosed(false); }}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: VCResources.PullRequest_DeleteSourceBranch,
                    subText: StringUtils.format(VCResources.PullRequest_DeleteSourceBranch_Warning_Text, this.state.pullRequest.sourceFriendlyName),
                }}
                modalProps={{
                    isBlocking: true,
                    containerClassName: 'vc-dialog'
                }}
            >
                <DialogFooter>
                    <PrimaryButton onClick={() => { this._deleteSourceBranchClosed(true); }} text={VCResources.OKLabel} />
                    <DefaultButton onClick={() => { this._deleteSourceBranchClosed(false); }} text={VCResources.Cancel} />
                </DialogFooter>
            </Dialog>
        );
    }

    private _createUnpublishDialog(): JSX.Element {
        // warn user that unpublishing will reset all votes
        return (
            <Dialog
                hidden={false}
                onDismiss={() => { this._unpublishDialogClosed(false); }}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: VCResources.PullRequest_ResetVotesOnDraft_Warning_Title,
                    subText: VCResources.PullRequest_ResetVotesOnDraft_Warning_Text,
                }}
                modalProps={{
                    isBlocking: true,
                    containerClassName: 'vc-dialog'
                }}
            >
                <DialogFooter>
                    <PrimaryButton onClick={() => { this._unpublishDialogClosed(true); }} text={VCResources.OKLabel} />
                    <DefaultButton onClick={() => { this._unpublishDialogClosed(false); }} text={VCResources.Cancel} />
                </DialogFooter>
            </Dialog>);
    }

    private _completePullRequest = () => {
        const pullRequest = this.state.pullRequest;

        if (pullRequest.externallyMerged) {
            // Already merged; skip the merge dialog and mark the PR completed
            Flux.instance().actionCreator.pullRequestActionCreator.completePullRequest(
                pullRequest.pullRequestContract(),
                {} as VCContracts.GitPullRequestCompletionOptions
            );
        }
        else {
            this._invokeActionsBeforeShowingCompletionDialog(pullRequest);

            Flux.instance().actionCreator.completeMergeActionCreator.openCompleteMergeDialog();
            this.setState((prevState, props) => {
                const nextState = { ...prevState };
                nextState.showCompleteDialog = true;
                nextState.useAutoComplete = false;
                return nextState;
            });
        }
    }

    // These are some things we should update whenever we're going to show the completion dialog
    private _invokeActionsBeforeShowingCompletionDialog(pullRequest: IPullRequest) {
        // fire an action to query for branch status again
        Flux.instance().actionCreator.pullRequestActionCreator.queryBranchStatus(pullRequest.pullRequestContract());

        // also query again for work item transitions (work items may have changed since the last time we opened this dialog)
        Flux.instance().actionCreator.workItemActionCreator.queryAssociatedWorkItemTransitions(this.state.workItems);

        // Check to see if resolved conflicts are present
        Flux.instance().actionCreator.conflictActionCreator.updatePullRequestHasResolvedConflicts(pullRequest.pullRequestId);
    }

    private _handleCompleteMergeResult = (completionOptions?: VCContracts.GitPullRequestCompletionOptions) => {
        const pullRequest = this.state.pullRequest;

        this.setState({ showCompleteDialog: false });

        if (completionOptions) {
            if (this.state.useAutoComplete) {
                completionOptions.bypassPolicy = false;
                Flux.instance().actionCreator.pullRequestActionCreator.updatePullRequestAutoCompletion(
                    pullRequest.pullRequestId,
                    true,
                    completionOptions);
            }
            else {
                Flux.instance().actionCreator.pullRequestActionCreator.completePullRequest(pullRequest.pullRequestContract(), completionOptions);
            }
        }
    }

    private _abandonPullRequest = (): void => {
        const pullRequest = this.state.pullRequest;
        this._publishTelemetry(CustomerIntelligenceConstants.PullRequestActionMenuOption.Abandon);
        Flux.instance().actionCreator.pullRequestActionCreator.abandonPullRequest(pullRequest.pullRequestId);
    }

    private _reactivatePullRequest = (): void => {
        const pullRequest = this.state.pullRequest;
        Flux.instance().actionCreator.pullRequestActionCreator.reactivatePullRequest(pullRequest.pullRequestId);
    }

    private _publishPullRequest = (): void => {
        const pullRequest = this.state.pullRequest;
        Flux.instance().actionCreator.pullRequestActionCreator.publishPullRequest(pullRequest.pullRequestId);
    }

    private _unpublishPullRequestBegin = (): void => {
        // User wants to unpublish. If this would reset votes, first warn them in a dialog.

        if (this.state.reviewerItems.some(r => r.hasVote)) // some people have voted
        {
            this.setState({ showResetVotesDialog: true});
            return;
        }

        this._unpublishPullRequest();
    }

    private _unpublishDialogClosed = (confirm: boolean): void => {
        this.setState({ showResetVotesDialog: false });

        if (confirm) {
            this._unpublishPullRequest();
        }
    }

    private _deleteSourceBranchClosed = (confirm: boolean): void => {
        this.setState({ showDeleteBranchDialog: false });

        if (confirm) {
            Flux.instance().actionCreator.pullRequestActionCreator.deleteSourceRef(
                this.state.pullRequest.pullRequestContract());
        }
    }

    private _unpublishPullRequest = (): void => {
        const pullRequest = this.state.pullRequest;
        Flux.instance().actionCreator.pullRequestActionCreator.unpublishPullRequest(pullRequest.pullRequestId);
    }

    private _setAutoComplete = () => {
        this._invokeActionsBeforeShowingCompletionDialog(this.state.pullRequest);

        Flux.instance().actionCreator.completeMergeActionCreator.openCompleteMergeDialog();
        this.setState({
            showCompleteDialog: true,
            useAutoComplete: true
        });
    }

    private _getPrCreationProps(): OperationCompletedProps {
        return {
            primaryButtonText: VCResources.PullRequest_CreatePullRequestTitle,
            defaultButtonText: VCResources.AsyncGitOperationTracker_Close,
        };
    }

    private _cancelAutoComplete = () => {
        const pullRequest = this.state.pullRequest;

        Flux.instance().actionCreator.pullRequestActionCreator.updatePullRequestAutoCompletion(
            pullRequest.pullRequestId,
            false,
            pullRequest.pullRequestContract().completionOptions);
    }

    private _publishTelemetry = (option: CustomerIntelligenceConstants.PullRequestActionMenuOption) => {
        const pullRequest = this.state.pullRequest;

        Telemetry.publishEvent(new Telemetry.TelemetryEventData(
            CustomerIntelligenceConstants.VERSION_CONTROL_AREA,
            CustomerIntelligenceConstants.PULL_REQUEST_ACTION_DROP_MENU_ACTION,
            {
                menuOption: option,
                pullRequestId: pullRequest.pullRequestId,
            }));
    }

    private _onStoresChanged = () => {
        this.setState(this._getStateFromStores());
    }

    private _getStateFromStores(): IPullRequestActionBarState {
        const loading =
            Flux.instance().storesHub.clientPolicyEvaluationStore.isLoading() ||
            Flux.instance().storesHub.featureAvailabilityStore.isLoading() ||
            Flux.instance().storesHub.pullRequestDetailStore.isLoading() ||
            Flux.instance().storesHub.contextStore.isLoading() ||
            Flux.instance().storesHub.reviewersStore.isLoading() ||
            Flux.instance().storesHub.permissionsStore.isLoading();

        if (loading) {
            return {
                loading
            } as IPullRequestActionBarState;
        }

        const policiesAreLoading = Flux.instance().storesHub.clientPolicyEvaluationStore.isLoading();
        const policiesState = Flux.instance().storesHub.clientPolicyEvaluationStore.state;
        const hasRejectedPolicies = policiesState.hasRejectedBlockingPolicies;
        const policyCount = { totalPolicies: policiesState.clientPolicyEvaluations.length } as PolicyCount;

        return {
            policiesAreLoading,
            hasRejectedPolicies,
            policyCount,
            workItems: Flux.instance().storesHub.relatedWorkItemsStore.getWorkItems(),
            showCompleteDialog: this.state ? this.state.showCompleteDialog : false,
            useAutoComplete: this.state ? this.state.useAutoComplete : false,
            liveUpdateEnabled: Flux.instance().storesHub.navigationStore.getIsLiveUpdateEnabled(),
            transitionWorkItemsIsEnabled: true,
            pullRequest: Flux.instance().storesHub.pullRequestDetailStore.getPullRequestDetail(),
            repositoryContext: Flux.instance().storesHub.contextStore.getRepositoryContext(),
            tfsContext: Flux.instance().storesHub.contextStore.getTfsContext(),
            followStatus: Flux.instance().storesHub.followsStore.getFollowStatus(),
            reviewerItems: Flux.instance().storesHub.reviewersStore.getReviewers(),
            isVotePrimaryAction: Flux.instance().storesHub.reviewersStore.isVotePrimaryAction(),
            permissions: Flux.instance().storesHub.permissionsStore.getPermissions(),
            showResetVotesDialog: this.state ? this.state.showResetVotesDialog : false,
            showDeleteBranchDialog: this.state ? this.state.showDeleteBranchDialog : false,
            loading,
        }
    }
}
